import { Component, Injectable, inject } from '@angular/core';
import { NewplaBaseComponent2, NewplaPageUtils } from '@newpla/ng-common';
import { Bs5ModalCtrl } from '@newpla/ng-lib';
import { NpTaskAll } from '@newpla/ts-common';
import { CollectionView, DataMap, NpFlexGridCtrl } from '@newpla/wijmo';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NpxCommonModule } from '../../../../newpla-npx-ng17/src/_shared-modules/npx-common.module';
import { NpxFormsModule } from '../../../../newpla-npx-ng17/src/_shared-modules/npx-forms.module';
import { NpxWijmoGridModule } from '../../../../newpla-npx-ng17/src/_shared-modules/npx-wijmo-grid.module';
import { SlsNovaPageLoading } from '../../config/sls-nova-page-loading';

/**
 * 출고 검증 결과 모달 (출고요청 검수, 작업지시 생성 검증 등 PdiOutCheckResult 목록). 결과 목록(checkResults)을 넘겨 연다.
 * showOk: 처음부터 통과 줄도 보일지. errorNote: 오류가 있을 때 요약 옆 안내 (기본 "오류가 있으면 출고할 수 없습니다.")
 */
@Injectable({
  providedIn: 'root'
})
export class PdiOutCheckResultModalOpenService {
  private ngbModal = inject(NgbModal);
  openModal(checkResults: any[], opts?: { title?: string, showOk?: boolean, errorNote?: string }) {
    let modal = this.ngbModal.open(PdiOutCheckResultModalComponent, { size: 'lg', scrollable: true, backdrop: 'static' });
    modal.componentInstance.checkResults = checkResults;
    modal.componentInstance.title = opts?.title;
    modal.componentInstance.showOk = opts?.showOk ?? false;
    modal.componentInstance.errorNote = opts?.errorNote;
    return modal.result;
  }
}

@Component({
  selector: 'pdi-outbound-order-check-result-modal',
  standalone: true,
  imports: [NpxCommonModule, NpxFormsModule, NpxWijmoGridModule],
  templateUrl: './pdi-outbound-order-check-result-modal.component.html',
  styleUrl: './pdi-outbound-order-check-result-modal.component.scss'
})
export class PdiOutCheckResultModalComponent extends NewplaBaseComponent2 {

  ///////////////////////////////////////////////////////////////////////////////////////////////////// BROWSER INIT
  browserInited: boolean = false;
  override browserNgOnInit = async () => {
    try {
      const taskAll = new NpTaskAll();
      taskAll.add(SlsNovaPageLoading.wait());
      await taskAll.run();

      this.modalInit();
      this.browserInited = true;
    } catch (e) {
      NewplaPageUtils.browserInitError(e);
      this.activeModal.dismiss();
    }
  };

  ///////////////////////////////////////////////////////////////////////////////////////////////////// DATA

  checkResults: any[] = [];
  title?: string;
  /** 오류가 있을 때 요약 옆 안내. 없으면 출고 기준 문구 */
  errorNote?: string;

  dataMap = {
    // 서버 PdiOutCheckType · PdiOutCheckCategory (base pdi.common.type)와 같은 값
    type: new DataMap([{ id: 'ERROR', text: '오류' }, { id: 'ALERT', text: '주의' }, { id: 'OK', text: '통과' }], 'id', 'text'),
    category: new DataMap([{ id: 'VEHICLE', text: '차량' }, { id: 'WORK_ORDER', text: 'WO' }, { id: 'WORK_TASK', text: '작업' },
      { id: 'DELIVERY', text: '배송' }, { id: 'TC', text: 'TC' }], 'id', 'text'),
  }

  get errorCnt(): number {
    return this.checkResults.filter(r => r.type === 'ERROR').length;
  }

  get alertCnt(): number {
    return this.checkResults.filter(r => r.type === 'ALERT').length;
  }

  /** 통과 건수. 옛 검사기(V·W·D)는 OK를 내지 않아 0이고, 새 검수 결과가 저장되면 채워진다 */
  get okCnt(): number {
    return this.checkResults.filter(r => r.type === 'OK').length;
  }

  ///////////////////////////////////////////////////////////////////////////////////////////////////// INJECT

  activeModal = inject(NgbActiveModal);
  modalCtrl = new Bs5ModalCtrl({ drag: true, fullscreen: true });

  // 그리드 /////////////////////////////////////////////////////////////////////////////////////////////////////

  flexGrid1Cv: CollectionView = new CollectionView([], {});
  flexGrid1Ctrl: NpFlexGridCtrl = new NpFlexGridCtrl({
    selector: 'MULTI',
    flexGridId: window.location.pathname + '#pdiOutboundOrderCheck#flexGrid1',
    excelDownload: false,
    excelFileName: '출고요청_검증결과'
  });

  /** 통과(OK) 줄도 보일지. 기본은 오류·주의만 */
  showOk = false;

  modalInit() {
    if (!this.checkResults) this.checkResults = [];
    this.refreshRows();
  }

  /** 표에 보일 줄: 기본은 오류·주의, 토글을 켜면 통과까지. 오류 → 주의 → 통과 순, 같은 수준은 규칙 순서(ord) */
  refreshRows() {
    const rank: any = { ERROR: 0, ALERT: 1, OK: 2 };
    this.flexGrid1Cv.sourceCollection = this.checkResults
      .filter(r => this.showOk || r.type !== 'OK')
      .sort((a, b) => (rank[a.type] ?? 9) - (rank[b.type] ?? 9) || (a.ord ?? 0) - (b.ord ?? 0));
  }

  /** 수준 셀 배지 색 */
  /** 수준 배지 색: 오류(빨강) / 통과(초록) / 주의(노랑) */
  typeBadgeClass(type: string): string {
    if (type === 'ERROR') return 'bg-danger-subtle text-danger-emphasis border-danger-subtle';
    if (type === 'OK') return 'bg-success-subtle text-success-emphasis border-success-subtle';
    return 'bg-warning-subtle text-warning-emphasis border-warning-subtle';
  }

}
